<template>
	<nav
		ref="tocRef"
		class="news-toc"
		:class="{ 'news-toc--open': isOpen }"
		:data-topbar-visible="isTopBarVisible"
		:aria-label="$t('pages.news.toc_title')">
		<p class="news-toc__title">{{ $t('pages.news.toc_title') }}</p>
		<button type="button" class="news-toc__toggle" :aria-expanded="isOpen" @click="isOpen = !isOpen">
			<span class="news-toc__toggle-label">{{ activeLabel }}</span>
			<GlobalIcon name="arrow-down" :height="14" class="news-toc__toggle-icon" :class="{ 'news-toc__toggle-icon--active': isOpen }" />
		</button>
		<ul class="news-toc__list">
			<li v-for="item in items" :key="item.id" class="news-toc__item">
				<a
					:href="`#${item.id}`"
					class="news-toc__link"
					:class="{ 'news-toc__link--active': item.id === activeSectionId }"
					:aria-current="item.id === activeId ? 'location' : undefined"
					@click="scrollToHeading($event, item.id)">
					{{ item.label }}
				</a>
				<ul v-if="item.children.length && item.id === activeSectionId" class="news-toc__sublist">
					<li v-for="child in item.children" :key="child.id" class="news-toc__item">
						<a
							:href="`#${child.id}`"
							class="news-toc__link news-toc__link--sub"
							:class="{ 'news-toc__link--active': child.id === activeId }"
							:aria-current="child.id === activeId ? 'location' : undefined"
							@click="scrollToHeading($event, child.id)">
							<GlobalIcon v-if="child.id === activeId" name="arrow-right" :height="12" class="news-toc__arrow" />
							{{ child.label }}
						</a>
					</li>
				</ul>
			</li>
		</ul>
	</nav>
</template>

<script setup lang="ts">
const props = defineProps<{
	items: INewsTocItem[]
}>()

const $t = useI18n().t
const isTopBarVisible = useIsTopBarVisible()
const isOpen = ref(false)
const tocRef = ref<HTMLElement | null>(null)
const activeId = ref<string | null>(null)

const ACTIVE_OFFSET = 200

const flatItems = computed(() => props.items.flatMap((item) => [item, ...item.children]))

const activeSectionId = computed(
	() => props.items.find((item) => item.id === activeId.value || item.children.some((child) => child.id === activeId.value))?.id,
)

const activeLabel = computed(() => flatItems.value.find((item) => item.id === activeId.value)?.label ?? $t('pages.news.toc_title'))

const updateActive = () => {
	const headings = flatItems.value.map((item) => document.getElementById(item.id)).filter((el): el is HTMLElement => !!el)
	if (!headings.length) return

	const isAtBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2
	if (isAtBottom) {
		activeId.value = headings[headings.length - 1].id
		return
	}

	let current: string | null = null
	for (const heading of headings) {
		if (heading.getBoundingClientRect().top > ACTIVE_OFFSET) break
		current = heading.id
	}
	activeId.value = current
}

let frame = 0
const onScroll = () => {
	cancelAnimationFrame(frame)
	frame = requestAnimationFrame(updateActive)
}

useEventListener('scroll', onScroll, { passive: true })
useEventListener('resize', onScroll, { passive: true })
onClickOutside(tocRef, () => (isOpen.value = false))
onMounted(updateActive)
onBeforeUnmount(() => cancelAnimationFrame(frame))

const scrollToHeading = (event: MouseEvent, id: string) => {
	const heading = document.getElementById(id)
	if (!heading) return

	event.preventDefault()
	isOpen.value = false
	heading.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<style lang="scss" src="./Toc.scss"></style>
